import "./styles.css";
import "./adaptive-signup-form.css";
import "./terms.css";
import "./terms-dialog.css";
import "./terms-cinematic.css";
import "./brand.css";

import { NAVIGATION_CONFIG, NAVIGATION_ITEMS } from "./config/navigation.js";
import { PROFILE_PICTURE_POLICY, SIGNUP_FIELDS } from "./config/signup-fields.js";
import { SIGNUP_CONCEPTS, conceptFromPath } from "./config/production-concepts.js";
import { REGISTRATION_STATE } from "./config/registration-state.js";
import { IDENTITY_UNIQUENESS_POLICY } from "./config/identity-uniqueness-policy.js";
import { ACCOUNT_ELIGIBILITY_POLICY } from "./config/account-eligibility-policy.js";
import { brandLinkMarkup } from "./config/brand.js";
import { createSignupForm } from "./components/adaptive-signup-form.js";
import { createGhostlineAccountServiceAdapter } from "./integration/account-service-adapter.js";
import { createLivingCompositor } from "./visuals/living-compositor.js";
import { createApprovedVfxMaster } from "./visuals/approved-vfx-alpha-master.js";

import { bootTermsReaderPage } from "./pages/terms-reader-page.js";
import { bootContactPage } from "./pages/contact-page.js";
const params = new URLSearchParams(window.location.search);
const still = params.get("still") === "1";
const path = window.location.pathname.replace(/\/+$/, "") || "/";
const pageKind = path === "/terms" ? "terms" : path === "/contact" ? "contact" : "signup";
const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
let reducedMotion = motionPreference.matches;
let concept = conceptFromPath(window.location.pathname);
let formController;
let compositor;
let vfxController;
let statusTimer;
let visualReadyValue = false;
let visualReadyToken = 0;
let registrationState = REGISTRATION_STATE;
let registrationSnapshot = null;
let userMotionPaused = false;
const registrationAdapter = createGhostlineAccountServiceAdapter({
  activation: import.meta.env.VITE_REGISTRATION_ACTIVATION || "disabled",
  deploymentTier: import.meta.env.VITE_DEPLOYMENT_TIER || "production",
  apiBaseUrl: import.meta.env.VITE_REGISTRATION_API_BASE || "/api/account/",
  browserOrigin: window.location.origin,
});

function navMarkup() {
  return NAVIGATION_ITEMS.map((item) => `
    <a href="${item.href}" data-nav-id="${item.id}"${item.id === pageKind ? ' aria-current="page"' : ""}>
      <span>${item.label}</span><i aria-hidden="true"></i>
    </a>`).join("");
}

function conceptLinksMarkup() {
  return SIGNUP_CONCEPTS.map((item, index) => `
    <a class="concept-option" href="${item.route}" data-concept-id="${item.id}"${item.id === concept.id ? ' aria-current="page"' : ""}>
      <span>0${index + 1}</span><strong>${item.shortName}</strong><i aria-hidden="true"></i>
    </a>`).join("");
}

function mediaMotionAvailable() {
  return !still && !reducedMotion;
}

function mediaMotionAllowed() {
  return mediaMotionAvailable() && !userMotionPaused;
}

function motionMode() {
  if (still) return "static";
  if (reducedMotion) return "reduced";
  return userMotionPaused ? "paused" : "full";
}

function motionUiState() {
  if (still) {
    return { mode: "static", label: "STATIC QA", action: "CAPTURE MODE", disabled: true, pressed: false };
  }
  if (reducedMotion) {
    return { mode: "reduced", label: "REDUCED MOTION", action: "SYSTEM SETTING", disabled: true, pressed: false };
  }
  if (userMotionPaused) {
    return { mode: "paused", label: "VFX PAUSED", action: "RESUME", disabled: false, pressed: true };
  }
  return { mode: "live", label: "VFX LIVE", action: "PAUSE", disabled: false, pressed: false };
}

function motionControlMarkup() {
  const state = motionUiState();
  return `
    <button class="motion-control" type="button" data-motion-control data-motion-mode="${state.mode}"
      aria-pressed="${state.pressed}" ${state.disabled ? "disabled" : ""}>
      <span class="motion-control__signal" aria-hidden="true"><i></i><i></i><i></i></span>
      <span class="motion-control__copy">
        <strong data-motion-label>${state.label}</strong>
        <small data-motion-action>${state.action}</small>
      </span>
    </button>`;
}

function syncMotionControl() {
  const control = document.querySelector("[data-motion-control]");
  if (!control) return;
  const state = motionUiState();
  control.dataset.motionMode = state.mode;
  control.setAttribute("aria-pressed", String(state.pressed));
  control.disabled = state.disabled;
  control.querySelector("[data-motion-label]").textContent = state.label;
  control.querySelector("[data-motion-action]").textContent = state.action;
  control.setAttribute(
    "aria-label",
    state.disabled ? `${state.label}: ${state.action}` : `${state.action.toLowerCase()} background effects`,
  );
}

function rendererStatus(stats = compositor?.getStats?.()) {
  if (still) return "DETERMINISTIC VFX STILL";
  if (reducedMotion) return "REDUCED MOTION // STATIC MASTER";
  if (userMotionPaused) return "SCENE PAUSED // RESUME AVAILABLE";
  return `${Math.round(stats?.fps || 0)} FPS // CINEMATIC VFX ONLINE`;
}

function filmSourcesMarkup(worldFilm) {
  if (!mediaMotionAvailable()) return "";
  return worldFilm.sources
    .map((source) => `<source src="${source.src}" type="${source.type}">`)
    .join("");
}

function worldMediaMarkup() {
  const worldFilm = concept.worldFilm;
  const allowMotion = mediaMotionAvailable();
  const playMotion = mediaMotionAllowed();
  const foreground = worldFilm.foreground;
  return `
    <div class="world-media" data-world-media aria-hidden="true">
      <video class="world-film" data-world-film data-world-film-id="${concept.id}"
        muted ${playMotion ? "autoplay" : ""} ${allowMotion ? "loop" : ""} playsinline tabindex="-1"
        preload="${allowMotion ? "metadata" : "none"}" poster="${worldFilm.poster}">
        ${filmSourcesMarkup(worldFilm)}
      </video>
      <div class="world-media__grade"></div>
    </div>
    <img class="world-foreground" data-world-foreground
      ${foreground ? `src="${foreground.src}"` : ""} alt="" aria-hidden="true" ${foreground ? "" : "hidden"}>`;
}

function approvedVfxMarkup() {
  const vfx = concept.cinematicVfx;
  return [
    '<div class="approved-vfx-layer" data-vfx-layer data-vfx-master-id="' + vfx.id + '"',
    ' data-vfx-mode="loading" data-vfx-state="idle" aria-hidden="true" inert>',
    '<canvas class="approved-vfx-output" data-vfx-output aria-hidden="true" hidden></canvas>',
    '<img class="approved-vfx-poster" data-vfx-poster src="' + vfx.poster + '" alt=""',
    ' aria-hidden="true" draggable="false" decoding="async">',
    '<video class="approved-vfx-master" data-vfx-master muted playsinline tabindex="-1"',
    ' aria-hidden="true" disablepictureinpicture preload="auto" poster="' + vfx.poster + '"></video>',
    '<div class="approved-vfx-response" aria-hidden="true"></div>',
    '</div>',
  ].join("");
}
function shellMarkup() {
  return `
    <main class="experience" id="main-content" data-concept="${concept.id}" data-registration-state="${REGISTRATION_STATE.status}">
      <canvas class="living-canvas" data-living-canvas aria-hidden="true"></canvas>
      ${worldMediaMarkup()}
      ${approvedVfxMarkup()}
      <div class="atmosphere atmosphere--grain" aria-hidden="true"></div>
      <div class="atmosphere atmosphere--vignette" aria-hidden="true"></div>
      <div class="frame frame--top" aria-hidden="true"></div>
      <div class="frame frame--left" aria-hidden="true"></div>
      <div class="frame frame--right" aria-hidden="true"></div>
      <div class="frame frame--bottom" aria-hidden="true"></div>

      <header class="topbar">
        ${brandLinkMarkup()}
        <nav class="primary-nav" aria-label="Primary navigation">${navMarkup()}</nav>
        <div class="account-link"><span>${NAVIGATION_CONFIG.accountLabel}</span><span class="account-link__disabled" aria-disabled="true">${NAVIGATION_CONFIG.loginLabel}</span></div>
      </header>

      <section class="world-story" aria-labelledby="world-title">
        <div class="scene-toolbar">
          <div class="sector-tag"><span></span><b data-scene-label>${concept.sceneLabel}</b><i>REGISTRATION LOCKED</i></div>
          ${motionControlMarkup()}
        </div>
        <p class="world-eyebrow" data-eyebrow>${concept.eyebrow}</p>
        <h2 id="world-title" data-world-title>${concept.title}</h2>
        <p class="world-description" data-world-description>${concept.description}</p>
        <div class="world-readout">
          <span>RELEASE READINESS</span>
          <ul data-telemetry>${concept.telemetry.map((item) => `<li><i></i>${item}</li>`).join("")}</ul>
        </div>
      </section>

      <aside class="scene-coordinates" aria-hidden="true">
        <span>EXILED // SIGNUP</span><i></i><b>PUBLIC 07</b>
      </aside>

      <section class="form-stage" data-form-mount></section>

      <div class="preview-toast" role="status" aria-live="polite" hidden data-preview-toast></div>
      <div class="performance-badge" aria-hidden="true"><i></i><span data-renderer-status>SCENE INITIALIZING</span></div>
    </main>`;
}

function applyConcept(nextConcept, { replaceHistory = false } = {}) {
  concept = nextConcept;
  const root = document.querySelector(".experience");
  root.dataset.concept = concept.id;
  root.style.setProperty("--scene-accent", concept.palette.accent);
  root.style.setProperty("--scene-secondary", concept.palette.secondary);
  root.style.setProperty("--scene-danger", concept.palette.danger);
  root.querySelector("[data-scene-label]").textContent = concept.sceneLabel;
  root.querySelector("[data-eyebrow]").textContent = concept.eyebrow;
  root.querySelector("[data-world-title]").textContent = concept.title;
  root.querySelector("[data-world-description]").textContent = concept.description;
  root.querySelector("[data-telemetry]").innerHTML = concept.telemetry.map((item) => `<li><i></i>${item}</li>`).join("");
  root.querySelectorAll("[data-concept-id]").forEach((link) => {
    if (link.dataset.conceptId === concept.id) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  });
  document.title = `Exiled Systems \u2014 ${concept.name} Signup`;
  syncWorldMedia(concept);
  formController?.setConcept(concept);
  compositor?.setConcept(concept);
  const vfxReady = vfxController?.setConcept(concept);
  if (vfxReady) beginVisualReadiness(vfxReady);
  if (replaceHistory) history.replaceState({ conceptId: concept.id }, "", `${concept.route}${window.location.search}`);
  window.__SIGNUP_V7__ = buildPublicContract();
}

function syncWorldMedia(nextConcept) {
  const root = document.querySelector(".experience");
  const film = root?.querySelector("[data-world-film]");
  const foreground = root?.querySelector("[data-world-foreground]");
  const media = nextConcept.worldFilm;
  if (!root || !film || !foreground || !media) return;

  const allowMotion = mediaMotionAvailable();
  const playMotion = mediaMotionAllowed();
  root.style.setProperty("--world-film-opacity", String(media.opacity ?? 0.72));
  root.style.setProperty("--world-film-position", media.objectPosition || "50% 50%");
  root.style.setProperty("--world-foreground-opacity", String(media.foreground?.opacity ?? 0));
  root.style.setProperty("--world-foreground-parallax", String(media.foreground?.parallax ?? 0));
  root.dataset.worldMotion = motionMode();

  film.dataset.worldFilmId = nextConcept.id;
  film.muted = true;
  film.playsInline = true;
  film.autoplay = playMotion;
  film.loop = allowMotion;
  film.preload = allowMotion ? "metadata" : "none";
  const posterChanged = film.getAttribute("poster") !== media.poster;
  if (posterChanged) film.setAttribute("poster", media.poster);

  const desiredSources = allowMotion ? media.sources : [];
  const currentSources = [...film.querySelectorAll("source")].map((source) => `${source.src}|${source.type}`);
  const nextSources = desiredSources.map((source) => `${new URL(source.src, location.href).href}|${source.type}`);
  if (posterChanged || currentSources.join(";") !== nextSources.join(";")) {
    film.pause();
    film.replaceChildren(...desiredSources.map((sourceConfig) => {
      const source = document.createElement("source");
      source.src = sourceConfig.src;
      source.type = sourceConfig.type;
      return source;
    }));
    film.load();
  }
  if (playMotion) film.play().catch(() => {});
  else film.pause();

  const foregroundConfig = media.foreground;
  if (foregroundConfig) {
    if (foreground.getAttribute("src") !== foregroundConfig.src) foreground.src = foregroundConfig.src;
    foreground.hidden = false;
  } else {
    foreground.hidden = true;
    foreground.removeAttribute("src");
  }
}

function beginVisualReadiness(promise) {
  const token = ++visualReadyToken;
  const root = document.querySelector(".experience");
  document.documentElement.dataset.ready = "loading";
  if (root) root.dataset.visualReady = "loading";
  const pending = Promise.resolve(promise).then(() => {
    if (token !== visualReadyToken) return true;
    visualReadyValue = true;
    document.documentElement.dataset.ready = "true";
    if (root) root.dataset.visualReady = "true";
    window.__SIGNUP_V7__ = buildPublicContract(true);
    return true;
  }, (error) => {
    if (token === visualReadyToken) {
      visualReadyValue = false;
      document.documentElement.dataset.ready = "error";
      if (root) root.dataset.visualReady = "error";
      window.__SIGNUP_V7__ = buildPublicContract(false);
    }
    throw error;
  });
  pending.catch(() => {});
  visualReadyValue = pending;
  window.__SIGNUP_V7__ = buildPublicContract(pending);
  return pending;
}
function buildPublicContract(ready = visualReadyValue) {
  return Object.freeze({
    conceptId: concept.id,
    ready,
    previewOnly: false,
    registration: { ...registrationState },
    registrationAdapter: registrationSnapshot
      ? {
          ready: registrationSnapshot.ready,
          status: registrationSnapshot.status,
          reason: registrationSnapshot.reason,
        }
      : { ready: false, status: "locked", reason: "configuration-not-fetched" },
    identityUniqueness: {
      ...IDENTITY_UNIQUENESS_POLICY,
      pendingReservation: { ...IDENTITY_UNIQUENESS_POLICY.pendingReservation },
      requiredActivationGates: [...IDENTITY_UNIQUENESS_POLICY.requiredActivationGates],
      identifiers: IDENTITY_UNIQUENESS_POLICY.identifiers.map((identifier) => ({ ...identifier })),
    },
    accountEligibility: {
      ...ACCOUNT_ELIGIBILITY_POLICY,
      publicSelfRegistration: { ...ACCOUNT_ELIGIBILITY_POLICY.publicSelfRegistration },
      age: { ...ACCOUNT_ELIGIBILITY_POLICY.age },
      email: {
        ...ACCOUNT_ELIGIBILITY_POLICY.email,
        acceptedProviders: ACCOUNT_ELIGIBILITY_POLICY.email.acceptedProviders.map((provider) => ({ ...provider })),
        allowedDomains: [...ACCOUNT_ELIGIBILITY_POLICY.email.allowedDomains],
      },
      publicIdentity: {
        ...ACCOUNT_ELIGIBILITY_POLICY.publicIdentity,
        coveredFields: [...ACCOUNT_ELIGIBILITY_POLICY.publicIdentity.coveredFields],
        reservedBrandTokens: [...ACCOUNT_ELIGIBILITY_POLICY.publicIdentity.reservedBrandTokens],
        abusiveBrandedNames: {
          ...ACCOUNT_ELIGIBILITY_POLICY.publicIdentity.abusiveBrandedNames,
          blockedTerms: [...ACCOUNT_ELIGIBILITY_POLICY.publicIdentity.abusiveBrandedNames.blockedTerms],
        },
      },
      requiredActivationGates: [...ACCOUNT_ELIGIBILITY_POLICY.requiredActivationGates],
    },
    navigation: NAVIGATION_ITEMS.map((item) => ({ ...item })),
    fields: SIGNUP_FIELDS.map((field) => ({ ...field, ...(field.acceptedMimeTypes ? { acceptedMimeTypes: [...field.acceptedMimeTypes] } : {}) })),
    profilePicturePolicy: {
      ...PROFILE_PICTURE_POLICY,
      acceptedMimeTypes: [...PROFILE_PICTURE_POLICY.acceptedMimeTypes],
      includedInRegistrationPayload: false,
    },
    getFormState: () => formController?.getState?.() || {
      phase: "identity",
      complete: false,
      values: {},
      profilePicture: {
        selected: false,
        valid: false,
        type: null,
        size: 0,
        localOnly: true,
        includedInSubmission: false,
      },
    },
    rendererStats: compositor?.getStats?.() || { renderer: "canvas-2d", frames: 0, still },
    worldMedia: {
      conceptId: concept.id,
      poster: concept.worldFilm.poster,
      sources: concept.worldFilm.sources.map((source) => ({ ...source })),
      motion: motionMode(),
    },
    cinematicVfx: vfxController?.getState?.() || {
      assetId: concept.cinematicVfx.id,
      conceptId: concept.id,
      mode: still ? "deterministic" : reducedMotion ? "static" : "full",
      readyState: "loading",
      sourceCount: 0,
      paused: true,
      requestedTime: params.has("time") ? Number(params.get("time")) : null,
      mappedTime: null,
      currentTime: 0,
      duration: concept.cinematicVfx.duration,
      presentedFrames: 0,
      localOnly: true,
      error: null,
    },
    motionControl: {
      mode: motionMode(),
      paused: userMotionPaused,
      available: mediaMotionAvailable(),
    },
    routes: SIGNUP_CONCEPTS.map((item) => item.route),
  });
}

function toast(message) {
  const node = document.querySelector("[data-preview-toast]");
  window.clearTimeout(statusTimer);
  node.textContent = message;
  node.hidden = false;
  statusTimer = window.setTimeout(() => { node.hidden = true; }, 3200);
}

function installInteractions() {
  document.addEventListener("click", (event) => {
    const motionControl = event.target.closest("[data-motion-control]");
    if (motionControl) {
      event.preventDefault();
      if (!mediaMotionAvailable()) return;
      userMotionPaused = !userMotionPaused;
      syncWorldMedia(concept);
      compositor?.setPaused?.(userMotionPaused);
      const vfxState = vfxController?.setPaused?.(userMotionPaused);
      Promise.resolve(vfxState).then(() => {
        syncMotionControl();
        const rendererLabel = document.querySelector("[data-renderer-status]");
        if (rendererLabel) rendererLabel.textContent = rendererStatus();
        window.__SIGNUP_V7__ = buildPublicContract();
      }).catch(() => {
        userMotionPaused = true;
        syncWorldMedia(concept);
        compositor?.setPaused?.(true);
        syncMotionControl();
        const rendererLabel = document.querySelector("[data-renderer-status]");
        if (rendererLabel) rendererLabel.textContent = "VFX PAUSED // PLAYBACK ERROR";
        window.__SIGNUP_V7__ = buildPublicContract();
      });
      syncMotionControl();
      return;
    }
    const conceptLink = event.target.closest("[data-concept-id]");
    if (conceptLink) {
      event.preventDefault();
      const next = SIGNUP_CONCEPTS.find((item) => item.id === conceptLink.dataset.conceptId);
      if (!next || next.id === concept.id) return;
      history.pushState({ conceptId: next.id }, "", `${next.route}${window.location.search}`);
      applyConcept(next);
      compositor?.cue("transition");
      vfxController?.cue("transition");
      return;
    }
    const previewLink = event.target.closest("[data-locked-link]");
    if (previewLink) {
      event.preventDefault();
      toast(`${previewLink.textContent.trim()} is not available while account services are locked.`);
    }
  });
  window.addEventListener("popstate", () => applyConcept(conceptFromPath(window.location.pathname)));
  window.addEventListener("pointermove", (event) => {
    const normalizedX = event.clientX / window.innerWidth * 2 - 1;
    const normalizedY = event.clientY / window.innerHeight * 2 - 1;
    compositor?.setPointer(normalizedX, normalizedY);
    const root = document.querySelector(".experience");
    if (root && mediaMotionAllowed()) {
      root.style.setProperty("--scene-pointer-x", normalizedX.toFixed(4));
      root.style.setProperty("--scene-pointer-y", normalizedY.toFixed(4));
    }
  }, { passive: true });
  motionPreference.addEventListener("change", (event) => {
    reducedMotion = event.matches;
    syncWorldMedia(concept);
    const vfxReady = vfxController?.setReducedMotion(reducedMotion);
    if (vfxReady) beginVisualReadiness(vfxReady);
    const rendererLabel = document.querySelector("[data-renderer-status]");
    if (rendererLabel) rendererLabel.textContent = rendererStatus();
    syncMotionControl();
    if (window.__SIGNUP_V7__) window.__SIGNUP_V7__ = buildPublicContract();
  });
}

async function boot() {
  document.body.dataset.page = "signup";
  const app = document.querySelector("#app");
  app.innerHTML = shellMarkup();
  const root = document.querySelector(".experience");
  root.style.setProperty("--scene-accent", concept.palette.accent);
  root.style.setProperty("--scene-secondary", concept.palette.secondary);
  root.style.setProperty("--scene-danger", concept.palette.danger);

  compositor = createLivingCompositor(root.querySelector("[data-living-canvas]"), {
    concept,
    reducedMotion,
    still,
    onStats(stats) {
      const label = document.querySelector("[data-renderer-status]");
      if (label) {
        label.textContent = rendererStatus(stats);
      }
      if (window.__SIGNUP_V7__) window.__SIGNUP_V7__ = buildPublicContract();
    },
  });
  const rendererLabel = document.querySelector("[data-renderer-status]");
  if (rendererLabel) rendererLabel.textContent = rendererStatus();

  registrationSnapshot = await registrationAdapter.refresh();
  registrationState = registrationSnapshot.registration;
  formController = createSignupForm({
    mount: root.querySelector("[data-form-mount]"),
    schema: SIGNUP_FIELDS,
    concept,
    registration: registrationState,
    onSubmitRequest: (request) => registrationAdapter.submit(request),
    onCue(type) {
      compositor.cue(type);
      vfxController?.cue(type);
    },
    onPhaseChange(phase) { root.dataset.signupPhase = phase; },
  });
  installInteractions();
  applyConcept(concept, { replaceHistory: !SIGNUP_CONCEPTS.some((item) => item.route === window.location.pathname) });
  vfxController = createApprovedVfxMaster(root, {
    concept,
    reducedMotion,
    still,
  });
  syncMotionControl();
  beginVisualReadiness(vfxController.ready());
}

const app = document.querySelector("#app");
if (pageKind === "terms") bootTermsReaderPage(app, { still });
else if (pageKind === "contact") bootContactPage(app, { still });
else await boot();
